import React, { useEffect, useRef, useState } from 'react';
import {
  Mafs,
  Circle,
  Line,
  Point,
  Text,
  Polygon,
  Vector,
  Polyline,
} from 'mafs';
import * as Babel from '@babel/standalone';

// Helper function to create arc points
const createArc = (center, radius, startAngle, endAngle, numPoints = 50) => {
  const points = [];
  const angleStep = (endAngle - startAngle) / numPoints;
  for (let i = 0; i <= numPoints; i++) {
    const angle = startAngle + i * angleStep;
    const x = center[0] + radius * Math.cos((angle * Math.PI) / 180);
    const y = center[1] + radius * Math.sin((angle * Math.PI) / 180);
    points.push([x, y]);
  }
  return points;
};

// Arc component wrapper
const Arc = ({
  center,
  radius,
  startAngle,
  endAngle,
  color,
  weight,
  ...props
}) => {
  // Validate inputs
  if (!center || !Array.isArray(center) || center.length < 2) {
    console.warn(
      'Arc component: center must be an array with at least 2 elements'
    );
    return null;
  }

  if (typeof radius !== 'number' || radius <= 0) {
    console.warn('Arc component: radius must be a positive number');
    return null;
  }

  if (typeof startAngle !== 'number' || typeof endAngle !== 'number') {
    console.warn('Arc component: startAngle and endAngle must be numbers');
    return null;
  }

  const points = createArc(center, radius, startAngle, endAngle);
  return <Polyline points={points} color={color} weight={weight} {...props} />;
};

// Angle component wrapper - supports two APIs
const Angle = ({
  point,
  start,
  end,
  at,
  fromAngle,
  toAngle,
  radius = 0.5,
  ...props
}) => {
  // Support for direct angle API (fromAngle/toAngle)
  if (at !== undefined && fromAngle !== undefined && toAngle !== undefined) {
    if (!Array.isArray(at) || at.length < 2) {
      console.warn(
        'Angle component: at must be an array with at least 2 elements'
      );
      return null;
    }

    if (typeof fromAngle !== 'number' || typeof toAngle !== 'number') {
      console.warn('Angle component: fromAngle and toAngle must be numbers');
      return null;
    }

    return (
      <Arc
        center={at}
        radius={radius}
        startAngle={fromAngle}
        endAngle={toAngle}
        {...props}
      />
    );
  }

  // Support for coordinate-based API (point/start/end)
  if (!point || !start || !end) {
    console.warn(
      'Angle component requires either (at, fromAngle, toAngle) or (point, start, end) props'
    );
    return null;
  }

  if (!Array.isArray(point) || point.length < 2) {
    console.warn(
      'Angle component: point must be an array with at least 2 elements'
    );
    return null;
  }

  if (!Array.isArray(start) || start.length < 2) {
    console.warn(
      'Angle component: start must be an array with at least 2 elements'
    );
    return null;
  }

  if (!Array.isArray(end) || end.length < 2) {
    console.warn(
      'Angle component: end must be an array with at least 2 elements'
    );
    return null;
  }

  const startAngle =
    (Math.atan2(start[1] - point[1], start[0] - point[0]) * 180) / Math.PI;
  const endAngle =
    (Math.atan2(end[1] - point[1], end[0] - point[0]) * 180) / Math.PI;

  return (
    <Arc
      center={point}
      radius={radius}
      startAngle={startAngle}
      endAngle={endAngle}
      {...props}
    />
  );
};

// Component to render diagrams with Mafs support
const DiagramRenderer = React.memo(
  ({ diagram, code, height = 300, viewBox }) => {
    const diagramRef = useRef(null);
    const [isProcessed, setIsProcessed] = useState(false);

    useEffect(() => {
      if (!diagramRef.current || isProcessed) return;

      const processDiagram = async () => {
        try {
          // If we have 'code' prop directly, render it without HTML parsing
          if (code) {
            // Wrap the code in a function so return statement is valid
            const wrappedCode = `function MafsComponent(React, Circle, Line, Point, Text, Arc, Polygon, Angle, Vector, Polyline, Math) { ${code} }`;

            // Transform JSX to JavaScript using Babel
            const transformed = Babel.transform(wrappedCode, {
              presets: ['react'],
            }).code;

            // Create a function to execute the transformed code
            // eslint-disable-next-line no-new-func
            const executeCode = new Function(
              'React',
              'Circle',
              'Line',
              'Point',
              'Text',
              'Arc',
              'Polygon',
              'Angle',
              'Vector',
              'Polyline',
              'Math',
              transformed +
                '\nreturn MafsComponent(React, Circle, Line, Point, Text, Arc, Polygon, Angle, Vector, Polyline, Math);'
            );

            // Execute with proper Line object that has Segment
            const LineWithSegment = Object.assign(Line, {
              Segment: Line.Segment,
            });
            const elements = executeCode(
              React,
              Circle,
              LineWithSegment,
              Point,
              Text,
              Arc,
              Polygon,
              Angle,
              Vector,
              Polyline,
              Math
            );

            // Render directly
            const root = require('react-dom/client').createRoot(
              diagramRef.current
            );
            root.render(
              <Mafs
                height={height}
                viewBox={viewBox}
                preserveAspectRatio={false}
              >
                {elements}
              </Mafs>
            );
            setIsProcessed(true);
            return;
          }

          // Otherwise, process HTML diagram string
          if (!diagram) return;

          // First, inject the HTML
          diagramRef.current.innerHTML = diagram;

          // Then process Mafs components
          const componentPlaceholders = diagramRef.current.querySelectorAll(
            '[data-component="Mafs"]:not([data-component-processed])'
          );

          for (const placeholder of componentPlaceholders) {
            try {
              placeholder.setAttribute('data-component-processed', 'true');
              const propsString = placeholder.getAttribute('data-props');

              const heightMatch = propsString?.match(/"height":\s*(\d+)/);
              const height = heightMatch ? parseInt(heightMatch[1]) : 300;

              const viewBoxMatch = propsString?.match(
                /"viewBox":\s*(\{[^}]+\})/
              );
              let viewBox = undefined;
              if (viewBoxMatch) {
                try {
                  viewBox = JSON.parse(viewBoxMatch[1]);
                } catch (e) {
                  console.warn('Could not parse viewBox:', e);
                }
              }

              let code = '';
              if (propsString) {
                const codeStart = propsString.indexOf('"code":');
                if (codeStart !== -1) {
                  const afterCode = propsString.substring(codeStart + 7).trim();
                  const quoteStart = afterCode.indexOf('"');
                  if (quoteStart !== -1) {
                    let quoteEnd = quoteStart + 1;
                    let escaped = false;
                    while (quoteEnd < afterCode.length) {
                      if (afterCode[quoteEnd] === '\\' && !escaped) {
                        escaped = true;
                      } else if (afterCode[quoteEnd] === '"' && !escaped) {
                        break;
                      } else {
                        escaped = false;
                      }
                      quoteEnd++;
                    }
                    code = afterCode.substring(quoteStart + 1, quoteEnd);
                    code = code
                      .replace(/\\"/g, '"')
                      .replace(/\\\\/g, '\\')
                      .replace(/\\n/g, '\n')
                      .replace(/\\t/g, '\t');
                  }
                }
              }

              if (!code) throw new Error('No code found in Mafs props');

              const wrappedCode = `function MafsComponent(React, Circle, Line, Point, Text, Arc, Polygon, Angle, Vector, Polyline, Math) { ${code} }`;
              const transformed = Babel.transform(wrappedCode, {
                presets: ['react'],
              }).code;

              // eslint-disable-next-line no-new-func
              const executeCode = new Function(
                'React',
                'Circle',
                'Line',
                'Point',
                'Text',
                'Arc',
                'Polygon',
                'Angle',
                'Vector',
                'Polyline',
                'Math',
                transformed +
                  '\nreturn MafsComponent(React, Circle, Line, Point, Text, Arc, Polygon, Angle, Vector, Polyline, Math);'
              );

              const LineWithSegment = Object.assign(Line, {
                Segment: Line.Segment,
              });
              const elements = executeCode(
                React,
                Circle,
                LineWithSegment,
                Point,
                Text,
                Arc,
                Polygon,
                Angle,
                Vector,
                Polyline,
                Math
              );

              const root = require('react-dom/client').createRoot(placeholder);
              root.render(
                <Mafs
                  height={height}
                  viewBox={viewBox}
                  preserveAspectRatio={false}
                >
                  {elements}
                </Mafs>
              );
            } catch (error) {
              console.error('Error rendering Mafs:', error);
              placeholder.innerHTML = `<div style="color: red;">Erreur de rendu: ${error.message}</div>`;
            }
          }

          setIsProcessed(true);
        } catch (error) {
          console.error('Error processing diagram:', error);
        }
      };

      // Small delay to ensure DOM is ready
      const timer = setTimeout(processDiagram, 50);
      return () => clearTimeout(timer);
    }, [diagram, code, height, viewBox, isProcessed]);

    return <div ref={diagramRef} />;
  }
);

DiagramRenderer.displayName = 'DiagramRenderer';

export default DiagramRenderer;
